import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./baseQueryWithReauth";

export const tokenApi = createApi({
  reducerPath: "tokens",
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,
  tagTypes: ["TokenHistory", "TokenBalance", "TokenAnalytics"],
  endpoints: (builder) => ({
    // ── Mutations ──────────────────────────────────────────────────────────

    createBillUpload: builder.mutation({
      query: ({ token, ...body }) => ({
        url: "/api/admin/tokens/bill",
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body,
      }),
      invalidatesTags: ["TokenHistory", "TokenBalance", "TokenAnalytics"],
    }),

    createFreeUpload: builder.mutation({
      query: ({ token, ...body }) => ({
        url: "/api/admin/tokens/free-upload",
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body,
      }),
      invalidatesTags: ["TokenHistory", "TokenBalance", "TokenAnalytics"],
    }),

    createGetReturn: builder.mutation({
      query: ({ token, ...body }) => ({
        url: "/api/admin/tokens/get-return",
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body,
      }),
      invalidatesTags: ["TokenHistory", "TokenBalance", "TokenAnalytics"],
    }),

    // ── Queries ────────────────────────────────────────────────────────────

    // Monthly stacked analytics: { clinicId, year?, from?, to?, types? }
    // Response: { data: { monthly: [{ month: "YYYY-MM", byType: {…}, net: N }] } }
    getAnalyticsMonthly: builder.query({
      query: ({ token, clinicId, year, from, to, types }) => ({
        url: "/api/admin/tokens/analytics/monthly",
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: { clinicId, year, from, to, types },
      }),
      providesTags: ["TokenAnalytics"],
    }),

    // Per-type breakdown: { clinicId, from, to }
    // Response: { data: { breakdown: [{ type, total, count }] } }
    getAnalyticsByType: builder.query({
      query: ({ token, clinicId, from, to }) => ({
        url: "/api/admin/tokens/analytics/by-type",
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: { clinicId, from, to },
      }),
      providesTags: ["TokenAnalytics"],
    }),

    // Server accepts POST body: { clinicId, type, startDate, endDate, page, limit, operationType }
    // `types` (array) is a client-side concept → we send the first selected type only.
    // Pass `types: []` (empty) for "all types" (no filter).
    getTokenHistory: builder.query({
      query: ({ token, clinicId, types, from, to, search, limit = 100 }) => {
        const body = {
          clinicId,
          limit,
          page: 1,
        };
        // Server only supports single `type` filter; send first selected or omit for all
        if (types && types.length === 1) {
          body.type = types[0];
        }
        if (from) body.startDate = from;
        if (to) body.endDate = to;
        // `search` is not a server-side filter on /history; we handle it client-side
        return {
          url: "/api/admin/tokens/history",
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body,
        };
      },
      providesTags: ["TokenHistory"],
    }),
  }),
});

export const {
  useCreateBillUploadMutation,
  useCreateFreeUploadMutation,
  useCreateGetReturnMutation,
  useGetAnalyticsMonthlyQuery,
  useGetAnalyticsByTypeQuery,
  useGetTokenHistoryQuery,
  useLazyGetTokenHistoryQuery,
} = tokenApi;
